// Kit 04 · items 107 to 116. "One row height per table. No wobble."
//
//   107 table row     header row 36 · body row 44 · cell pad 16 · sort icon 14 · client row rule 3
//   108 sortable header      109 selection      110 expanded panel (760)
//   111 ranking       112 list row (44) · 113 meta row (56)
//   114 density       115 truncation            116 table states
//
// keys: "Arrow keys move between rows. Enter expands. The row is one tab stop,
//        not one per cell."
//
// That last clause decides the markup. A <table> with a focusable <button> in
// each cell gives a keyboard user five stops per row and forty on a screen;
// the specimen wants one. So a row is a single tab stop with roving focus
// between rows, and the cells are not interactive.
//
// ── 114, and why the row height is not a prop ──────────────────────────────
//
// "One height per table type: 36 header, 44 body. A row never changes height
// on hover, expand or select." A component that took a height would let one
// table have two. The heights are --ctl-field-adjacent tokens read off the
// redline, applied by the class, and a row that expands does so by revealing
// a SIBLING panel rather than by growing.

import { useRef } from "react";
import type { ReactNode } from "react";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, CircleAlert } from "lucide-react";
import { Icon } from "./icon";
import { cx, stateAttr } from "./kit-state";
import { SkeletonTable } from "./loading";
import { NOT_MEASURED } from "./strings";
import type { KitState, Stateful } from "./kit-state";

/* ═══ 107, 108 · the frame and its header ═══════════════════════════════════ */

export type SortDirection = "asc" | "desc";

export interface Column {
  key: string;
  label: string;
  /** Right-aligned, tabular. Every numeric column. */
  numeric?: boolean;
  sortable?: boolean;
}

export interface DataTableProps {
  label: string;
  columns: Column[];
  /** The grid template. One string so every row in the table shares it and no
      row can define its own columns. */
  template: string;
  sort?: { key: string; direction: SortDirection };
  onSort?: (key: string) => void;
  children: ReactNode;
  className?: string;
}

export function DataTable({
  label, columns, template, sort, onSort, children, className,
}: DataTableProps) {
  const body = useRef<HTMLDivElement | null>(null);

  // Arrow keys move between rows. Bound on the container rather than per row,
  // so a row that is added does not have to remember to opt in.
  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const rows = Array.from(body.current?.querySelectorAll<HTMLElement>('[role="row"][tabindex]') ?? []);
    if (rows.length === 0) return;
    event.preventDefault();
    const at = rows.indexOf(document.activeElement as HTMLElement);
    const step = event.key === "ArrowDown" ? 1 : -1;
    rows[Math.max(0, Math.min(rows.length - 1, at + step))]?.focus();
  }

  return (
    <div
      className={cx("k-table", className)}
      role="table"
      aria-label={label}
      style={{ ["--k-cols" as string]: template }}
    >
      <div className="k-table__head" role="row">
        {columns.map((col) => {
          const active = sort?.key === col.key;
          const content = (
            <>
              {col.label}
              {col.sortable ? (
                <Icon
                  icon={active && sort?.direction === "desc" ? ArrowDown : ArrowUp}
                  size="sm"
                  className={cx(!active && "k-table__sort--idle")}
                />
              ) : null}
            </>
          );
          return (
            <span
              key={col.key}
              role="columnheader"
              className={cx("k-table__th", col.numeric && "k-table__th--num")}
              aria-sort={active ? (sort?.direction === "asc" ? "ascending" : "descending") : undefined}
            >
              {col.sortable && onSort ? (
                <button type="button" className="k-table__sort" onClick={() => onSort(col.key)}>
                  {content}
                </button>
              ) : content}
            </span>
          );
        })}
      </div>
      <div className="k-table__body" ref={body} onKeyDown={onKeyDown}>
        {children}
      </div>
    </div>
  );
}

/* ═══ 107, 109, 111, 115 · the row ══════════════════════════════════════════
   111: "The client row carries a 3px accent rule and full-weight figures.
   Competitor rows stay muted. An unmeasured row is dashed."

   Three states of one row rather than three row components, because they are
   not exclusive: a client row can be unmeasured, and the ranking rule has to
   still be true of it.
   ═════════════════════════════════════════════════════════════════════════ */

export interface RowProps extends Stateful {
  /** The client's own row. Takes the accent rule. Exactly one per table. */
  client?: boolean;
  /** Nothing was measured for this row. Drawn dashed, never as zeroes. */
  unmeasured?: boolean;
  selected?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  /** Rendered under the row when expanded. Item 110. */
  panel?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function TableRow({
  client, unmeasured, selected, expanded, onToggle, panel, children, state, className,
}: RowProps) {
  const open = expanded || state === "expanded";
  return (
    <>
      <div
        role="row"
        // One tab stop for the whole row. Not one per cell.
        tabIndex={0}
        aria-selected={selected || state === "selected" || undefined}
        aria-expanded={onToggle ? open : undefined}
        className={cx("k-tr", className)}
        data-client={client || undefined}
        data-unmeasured={unmeasured || undefined}
        data-selected={selected || undefined}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" && onToggle) { e.preventDefault(); onToggle(); }
        }}
        {...stateAttr(state)}
      >
        <span className="k-td k-td--twisty" role="cell">
          {/* An unmeasured row has nothing to expand into, so it has no twisty.
              A disabled-looking chevron would invite the click anyway. */}
          {onToggle && !unmeasured ? <Icon icon={open ? ChevronDown : ChevronRight} size="sm" /> : null}
        </span>
        {children}
      </div>
      {open && panel ? <div className="k-tr__panel-wrap">{panel}</div> : null}
    </>
  );
}

/** A cell. `numeric` right-aligns it; the FIGURE inside carries the plane. */
export function Cell({
  numeric, truncate, children, className,
}: { numeric?: boolean; truncate?: boolean; children: ReactNode; className?: string }) {
  return (
    <span
      role="cell"
      className={cx("k-td", numeric && "k-td--num", truncate && "k-td--truncate", className)}
    >
      {children}
    </span>
  );
}

/** 115 · the brand column ellipsises. A cell that must never wrap and must
    never push the row wider than the table. */
export function BrandCell({ children, className }: { children: ReactNode; className?: string }) {
  return <Cell truncate className={className}>{children}</Cell>;
}

/* ═══ 110 · the expanded panel ══════════════════════════════════════════════
   redline: panel 760 · pad 24 · radius 12 · inset from the row
   keys:    Expands in place. Escape collapses and focus returns to the row.

   "The panel inherits the row it came from and adds only what the row could
   not hold. It never repeats the row." So the panel takes no brand name and no
   headline figure: those are in the row above it, two lines up, and repeating
   them is the roll-up-that-disagrees defect waiting to happen.
   ═════════════════════════════════════════════════════════════════════════ */

export function ExpandedPanel({
  title, meta, children, className,
}: { title: string; meta?: string; children: ReactNode; className?: string }) {
  return (
    <div className={cx("k-tr__panel", className)}>
      <div className="k-tr__panel-head">
        <span className="k-tr__panel-title">{title}</span>
        {meta ? <span className="k-meta">{meta}</span> : null}
      </div>
      {children}
    </div>
  );
}

/* ═══ 112, 113 · list rows ══════════════════════════════════════════════════
   redline: row 44 · meta row 56 · pad 16 · meta 12 muted

   Two heights, and which one you get is decided by whether there IS a meta
   line — not by a prop. A 56px row with no meta line is a 44px row with twelve
   pixels of nothing in it, and that is how a list comes to have two heights
   for the same content.
   ═════════════════════════════════════════════════════════════════════════ */

export function ListRow({
  title, meta, trailing, onSelect, className,
}: {
  title: ReactNode;
  /** Present makes it a 56px row; absent makes it 44. Derived, not passed. */
  meta?: ReactNode;
  trailing?: ReactNode;
  onSelect?: () => void;
  className?: string;
}) {
  return (
    <div
      role="row"
      tabIndex={0}
      className={cx("k-list-row", className)}
      data-meta={meta ? "true" : undefined}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === "Enter" && onSelect) { e.preventDefault(); onSelect(); } }}
    >
      <span className="k-list-row__text">
        <span className="k-list-row__title">{title}</span>
        {meta ? <span className="k-list-row__meta">{meta}</span> : null}
      </span>
      {trailing}
    </div>
  );
}

export function ListRows({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("k-list", className)} role="rowgroup">{children}</div>;
}

/* ═══ 116 · table states, painted in place ══════════════════════════════════
   Loading, empty and error all render INSIDE the table, under its header. A
   table that replaces itself with an empty state loses its columns, and the
   reader has to work out what they were looking at when it comes back.
   ═════════════════════════════════════════════════════════════════════════ */

export function TableLoading({ columns = 3, rows = 2 }: { columns?: number; rows?: number }) {
  return (
    <div className="k-table__state" role="row">
      <SkeletonTable rows={rows} columns={columns} />
    </div>
  );
}

export function TableEmpty({ children }: { children: ReactNode }) {
  return <div className="k-table__state k-table__state--quiet" role="row">{children}</div>;
}

export function TableError({ children }: { children: ReactNode }) {
  return (
    <div className="k-table__state k-table__state--error" role="row">
      <Icon icon={CircleAlert} size="sm" />
      {children}
    </div>
  );
}

export { NOT_MEASURED };
export type { KitState };
